// @ts-strict-ignore
/* eslint-disable no-underscore-dangle */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { JourneyNodeTypesMap, JourneyTypesMap } from '@usealloy/typegen';

import { typhonAjaxFetch } from '~/helpers/api';
import { typhonRoutes } from '~/helpers/routes';
import type { FeatureFlags } from '~/helpers/typescript';

import apiCalls from '../../helpers/apiCalls';
import {
  formatApplication,
  formatCase,
  getAlertReviewsByAlertManualReviewToken,
  getAllEvaluationTokens,
  getCasesByCaseToken,
  getCompletedAlertManualReviewTokens,
  getCompletedCaseTokens,
  getEntityDocumentsByDocToken,
  getEvaluationsByEvaluationToken,
  getEventsByEventRequestToken,
  getInitialSelectedJourneyApplicationStep,
  getJourneyActionVersionsByJourneyActionId,
  getJourneyApplicationNotes,
  getJourneyApplicationReviewCaseTokens,
  getOutcomeOptionsByReviewNodeId,
  getReviewReasonsByOutcomeId,
} from '../formatters';
import {
  mergeApplicationData,
  mergePrimaryApplicationData,
  mergeSecondaryApplicationData,
  setCompletedCaseByCaseToken,
  setEntityData,
  setEntityDocumentsByDocToken,
  setEvaluationByEvaluationToken,
} from './slice';
import type { SecondaryApplicationData } from './types';
import { getEOJReconWfNodeOutcomes } from './utils';

const {
  applicationReview: { getApplication },
  cases: { getCaseWorks },
  documents: { getDocumentTypes },
  entities: {
    getEntity,
    getEvaluation,
  },
  events: {
    getEventRequest,
  },
  journeys: {
    alerts: { getManualReview },
  },
  journeyActions: { getJourneyActions },
  settings: { getReviewReasons, getOutcomesNew: getOutcomes },
} = apiCalls;

const fetchPrimaryApplicationData = async ({
  journeyApplicationToken,
  journeyToken,
  typhonDomain,
  isReconWorkflowInGraphEnabled,
}) => {
  let applicationRaw;
  let documentTypes;
  let outcomeObjects;
  let reasonObjects;
  let journeyActions;

  try {
    [
      applicationRaw,
      documentTypes,
      { outcomes: outcomeObjects = [] },
      reasonObjects,
      journeyActions,
    ] = await Promise.all([
      getApplication({
        journeyApplicationToken,
        journeyToken,
        typhonDomain,
      }),
      getDocumentTypes({ typhonDomain }),
      getOutcomes({
        options: {
          q: {
            includeEventStatusOutcomes: false,
          },
          numRowsPerPage: 500,
        },
        typhonDomain,
      }),
      getReviewReasons(),
      /*
      There is an issue with journeyActionVersions where, if the version is updated, the roles who can review that action are not, so they all get permission denied.
      The two paths forward are:
      - (current solution) The Journey Action node in the JA references the version_id, and we check permissions against all other versions of that same action. This keep roles from needing to constantly update
      - The JA records the action_id rather than the action_version_id, but this may break older workflows if the action is updated too broadly.
      */
      getJourneyActions({ typhonDomain, activeOnly: false }),
    ]);
  } catch (e) {
    if (e.response.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to access this page.');
    } else if (e.response.status === 404) {
      throw new Error('The application data could not be found');
    } else {
      throw new Error('Failed to load primary application data');
    }
  }

  const outcomeCustomizations = outcomeObjects.reduce((customizationsMap, outcome) => {
    if (!outcome?.outcome) {
      return customizationsMap;
    }

    customizationsMap[outcome.outcome.toLowerCase()] = {
      outcome: outcome.outcome,
      outcomeColor: outcome.outcome_color ? `#${outcome.outcome_color}` : undefined,
      outcomeIcon: outcome.icon_name,
    };

    return customizationsMap;
  }, {});

  const applicationFormatted = formatApplication({ application: applicationRaw });
  const applicationEvents: any[] = applicationRaw._embedded.events;

  const applicationJourneyVersion = await typhonAjaxFetch(
    `${typhonDomain}${typhonRoutes.journeys.getVersion(journeyToken, applicationFormatted.journeyVersionNum)}`,
  );

  const journeyVersionHasOldOutcomes = !!applicationJourneyVersion.nodes.find(
    node => node.type === JourneyNodeTypesMap.OUTCOME && (node.config.outcome === 'Approved' || node.config.outcome === 'Denied'),
  );

  const eojReconNode = (
    applicationFormatted?.journeyType === JourneyTypesMap.APPLICATION
    && applicationJourneyVersion.terminal_reconciliation_workflow_node_id
  )
    ? applicationJourneyVersion.nodes.find(
      node => node.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
        && node.id === applicationJourneyVersion.terminal_reconciliation_workflow_node_id,
    )
    : null;

  const eojReconWfOutcomes = await getEOJReconWfNodeOutcomes({
    eojReconNode,
    isReconWorkflowInGraphEnabled,
    journeyApplicationState: applicationFormatted?.journeyApplicationState,
  });

  const outcomeOptionsByReviewNodeId = getOutcomeOptionsByReviewNodeId({
    applicationEvents,
    eojReconWfOutcomes,
    journeyActions,
    journeyType: applicationFormatted.journeyType,
    journeyVersionHasOldOutcomes,
    outcomes: outcomeObjects,
  });

  const reviewReasonByOutcomeId = getReviewReasonsByOutcomeId({
    outcomes: outcomeObjects,
    reasons: reasonObjects,
  });

  const journeyApplicationReviewCaseTokens = getJourneyApplicationReviewCaseTokens(
    applicationRaw._embedded.events,
  );

  const journeyActionVersionsByJourneyActionId = getJourneyActionVersionsByJourneyActionId(
    journeyActions,
  );

  return {
    application: applicationFormatted,
    applicationJourneyVersion,
    documentTypes,
    outcomeCustomizations,
    reviewOutcomeObjects: outcomeOptionsByReviewNodeId,
    reviewReasonObjectsByOutcomeId: reviewReasonByOutcomeId,
    journeyApplicationReviewCaseTokens,
    journeyActionVersionsByJourneyActionId,
  };
};

const formatDaffyEntityDocuments = entityData => (
  entityData?.documents
    ? entityData.documents
      .map(document => ({
        ...document,
        notes: document.notes || [],
        evaluation_tokens: document.evaluation_tokens || [],
        type: document.document_type,
        approved: document.approval_outcome,
      }))
    : []
);

const fetchSecondaryApplicationData = async ({
  allEvaluationTokens,
  completedAlertManualReviewTokens,
  completedCaseTokens,
  entityTokens,
  journeyApplicationReviewCaseTokens,
  journeyApplicationToken,
  journeyToken,
  typhonDomain,
}: {
  allEvaluationTokens: { evaluationToken: string; entityToken: string }[];
  completedAlertManualReviewTokens: string[];
  completedCaseTokens: string[];
  entityTokens: string[];
  journeyApplicationReviewCaseTokens: string[];
  journeyApplicationToken: string;
  journeyToken: string;
  typhonDomain: string;
}): Promise<SecondaryApplicationData> => {
  let evaluations;
  let completedAlertManualReviews;
  let completedCases;
  let journeyApplicationReviewCases;
  let entityData;
  let eventRequests;

  try {
    evaluations = await Promise.all(allEvaluationTokens.map(async ({
      evaluationToken,
      entityToken,
    }) => getEvaluation({
      entityToken,
      evaluationToken,
    }).catch(error => {
      /**
       * JAs run via journey batches do not currently handle validation errors as gracefully as
       * the POST JA endpoint. If validation errors are encountered when creating an eval as part
       * of a JA in a batch, there is an eval token left on the JA event record with no actual
       * eval record ever created. This results in an eval token on the JA details views that will
       * result in a `404`. Assuming this, we catch and handle the `404` error here to avoid breaking
       * the entire JA view in this scenario.
       */
      if (error?.response?.status === 404) {
        return null;
      }

      return error;
    })));

    const eventRequestTokens = evaluations
      .map(evaluation => evaluation?.event?.event_request_token)
      .filter(eventRequestToken => Boolean(eventRequestToken));

    [
      completedCases,
      completedAlertManualReviews,
      journeyApplicationReviewCases,
      entityData,
      eventRequests,
    ] = await Promise.all([
      Promise.all(completedCaseTokens.map(caseToken => getCaseWorks({
        caseToken,
        typhonDomain,
      }))),
      Promise.all(completedAlertManualReviewTokens.map(alertManualReviewToken => getManualReview({
        alertManualReviewToken,
        journeyApplicationToken,
        journeyToken,
        typhonDomain,
      }))),
      Promise.all(journeyApplicationReviewCaseTokens.map(caseToken => getCaseWorks({
        caseToken,
        typhonDomain,
      }))),
      Promise.all(entityTokens.map(entityToken => getEntity({
        entityToken,
      }))),
      Promise.all(eventRequestTokens.map(eventRequestToken => getEventRequest({
        eventRequestToken,
        typhonDomain,
      }))),
    ]);
  } catch (e) {
    if (e.response.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to access this page.');
    } else {
      throw new Error('Failed to load secondary application data');
    }
  }

  const eventsByEventRequestToken = getEventsByEventRequestToken(eventRequests);

  return {
    evaluationsByEvaluationToken: getEvaluationsByEvaluationToken(
      evaluations,
      eventsByEventRequestToken,
    ),
    notes: getJourneyApplicationNotes(entityData, journeyApplicationReviewCases),
    completedAlertManualReviewsByAlertManualReviewToken:
      getAlertReviewsByAlertManualReviewToken(completedAlertManualReviews),
    completedCasesByCaseToken: getCasesByCaseToken(completedCases),
    entities: entityData.map(entity => ({
      documentsByDocToken: getEntityDocumentsByDocToken(
        formatDaffyEntityDocuments(entity),
      ),
      id: entity.entity?.entity_id,
      notes: entity.notes?.filter(n => !n.journey_application_id),
    })),
  };
};

export const loadApplicationPageData = createAsyncThunk(
  'applicationPage/loadApplicationPageData',
  async ({
    journeyApplicationToken,
    journeyToken,
    typhonDomain,
    featureFlags,
  }: {
    journeyApplicationToken: string;
    journeyToken: string;
    typhonDomain: string;
    featureFlags: Partial<FeatureFlags>;
  }, { dispatch }) => {
    const isReconWorkflowInGraphEnabled = Boolean(featureFlags?.reconWorkflowInGraph);

    const {
      application,
      applicationJourneyVersion,
      documentTypes,
      outcomeCustomizations,
      reviewOutcomeObjects,
      reviewReasonObjectsByOutcomeId,
      journeyApplicationReviewCaseTokens,
      journeyActionVersionsByJourneyActionId,
    } = await fetchPrimaryApplicationData({
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
      isReconWorkflowInGraphEnabled,
    });

    const {
      childEntities,
      journeySteps,
    } = application;

    const selectedJourneyApplicationStep = getInitialSelectedJourneyApplicationStep(
      journeySteps,
      application.journeyType,
    );

    dispatch(mergePrimaryApplicationData({
      application,
      applicationJourneyVersion,
      documentTypes,
      entities: childEntities,
      outcomeCustomizations,
      // reviewOutcomeObjects has the required shape TS cannot infer it from the formatter.js file.
      reviewOutcomeObjects,
      reviewReasonObjectsByOutcomeId,
      selectedJourneyApplicationStep,
      journeyActionVersionsByJourneyActionId,
    }));

    const allEntityTokens = childEntities.map(e => e.token);
    const allEvaluationTokens = getAllEvaluationTokens(
      journeySteps,
      application.groupEntity.token,
      allEntityTokens,
    );

    // Build maps of evaluations by entity and journey steps by entity
    // because we will be loading everything by entity
    const mapEvaluationsByEntityToken = {};
    allEvaluationTokens.forEach(({ evaluationToken, entityToken }) => {
      if (mapEvaluationsByEntityToken[entityToken]) {
        mapEvaluationsByEntityToken[entityToken].push({ evaluationToken, entityToken });
      } else {
        mapEvaluationsByEntityToken[entityToken] = [{ evaluationToken, entityToken }];
      }
    });

    const mapJourneyStepsByEntityToken = {};
    journeySteps.forEach(step => {
      if (mapJourneyStepsByEntityToken[step.entityToken]) {
        mapJourneyStepsByEntityToken[step.entityToken].push(step);
      } else {
        mapJourneyStepsByEntityToken[step.entityToken] = [step];
      }
    });

    const nonSelectedEntitiesWithEvals = [application.groupEntity.token, ...allEntityTokens]
      .filter(token => token !== selectedJourneyApplicationStep.entityToken
      && mapEvaluationsByEntityToken[token]?.length > 0,
      );

    // Load only 3 entities initially: selected entity + any group evaluations + 1 additional entity
    const firstBatchEntityTokens = [
      selectedJourneyApplicationStep.entityToken,
      ...(nonSelectedEntitiesWithEvals.length > 0 ? nonSelectedEntitiesWithEvals.slice(0, 2) : []),
    ];

    const firstBatchEvaluationTokens = firstBatchEntityTokens.flatMap(
      entityToken => mapEvaluationsByEntityToken[entityToken] || [],
    );

    // Get journey steps for first 3 entities to load their cases/alerts
    const firstBatchJourneySteps = firstBatchEntityTokens.flatMap(
      entityToken => mapJourneyStepsByEntityToken[entityToken] || [],
    );

    const isAlert = application.journeyType === JourneyTypesMap.ALERT;

    dispatch(mergeSecondaryApplicationData(await fetchSecondaryApplicationData({
      allEvaluationTokens: firstBatchEvaluationTokens,
      completedAlertManualReviewTokens: isAlert
        ? getCompletedAlertManualReviewTokens(firstBatchJourneySteps)
        : [],
      completedCaseTokens: getCompletedCaseTokens(firstBatchJourneySteps),
      // fetch all entities to populate the entity cards
      entityTokens: allEntityTokens,
      // Fetch application-level cases in initial load (not entity-specific, only need once)
      journeyApplicationReviewCaseTokens,
      journeyApplicationToken: application.journeyApplicationToken,
      journeyToken: application.journeyToken,
      typhonDomain,
    })));

    // Background loading: Load remaining entities 1 at a time, every 2 seconds
    const entityTokensToFetchLater = nonSelectedEntitiesWithEvals.slice(2);

    entityTokensToFetchLater.forEach((entityToken, index) => {
      const entityEvaluationTokens = mapEvaluationsByEntityToken[entityToken] || [];
      const entityJourneySteps = mapJourneyStepsByEntityToken[entityToken] || [];

      setTimeout(async () => {
        dispatch(mergeSecondaryApplicationData(await fetchSecondaryApplicationData({
          allEvaluationTokens: entityEvaluationTokens,
          completedAlertManualReviewTokens: isAlert
            ? getCompletedAlertManualReviewTokens(entityJourneySteps)
            : [],
          completedCaseTokens: getCompletedCaseTokens(entityJourneySteps),
          // all entities were fetched in the initial fetch
          entityTokens: [],
          // Already loaded in initial fetch (application-level, not entity-specific)
          journeyApplicationReviewCaseTokens: [],
          journeyApplicationToken: application.journeyApplicationToken,
          journeyToken: application.journeyToken,
          typhonDomain,
        })));
      }, (index + 1) * 2000);
    });
  },
);

export const refreshApplicationData = createAsyncThunk(
  'applicationPage/refreshApplicationData',
  async ({ typhonDomain }: { typhonDomain: string }, { dispatch, getState }) => {
    const {
      applicationPage: { application: { journeyApplicationToken, journeyToken } },
    }: any = getState();
    dispatch(
      mergeApplicationData(
        formatApplication({
          application: await getApplication({
            journeyApplicationToken,
            journeyToken,
            typhonDomain,
          }),
        })));
  },
);

export const refreshCaseWorksByCaseToken = createAsyncThunk(
  'applicationPage/refreshCaseWorksByCaseToken',
  async (
    { caseToken, typhonDomain }: { caseToken: string; typhonDomain: string },
    { dispatch },
  ) => dispatch(
    setCompletedCaseByCaseToken(
      formatCase(
        await getCaseWorks({
          caseToken,
          typhonDomain,
        })))),
);

export const refreshEntityData = createAsyncThunk(
  'applicationPage/refreshEntityData',
  async ({ entityToken }: { entityToken: string }, { dispatch },
  ) => {
    const {
      entity: { entity_id: id },
      notes,
    } = await getEntity({ entityToken });
    return dispatch(setEntityData({ id, notes }));
  },
);

export const refreshEntityDocuments = createAsyncThunk(
  'applicationPage/refreshEntityDocuments',
  async ({ entityToken }: { entityToken: string }, { dispatch },
  ) => {
    dispatch(
      setEntityDocumentsByDocToken({
        entityDocumentsByDocToken: getEntityDocumentsByDocToken(
          formatDaffyEntityDocuments(
            await getEntity({ entityToken }),
          ),
        ),
        entityToken,
      }));
  },
);

export const refreshEvaluationDataByEvaluationToken = createAsyncThunk(
  'applicationPage/refreshEvaluationData',
  async (
    { evaluationToken, entityToken }: { evaluationToken: string; entityToken: string },
    { dispatch },
  ) => {
    dispatch(
      setEvaluationByEvaluationToken(
        await getEvaluation({
          entityToken,
          evaluationToken,
        })));
  },
);
